import { FloatingPortal, autoUpdate, useFloating } from '@floating-ui/react';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { maybePropN, prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { autoSizeSingleColumn } from 'components/Table/model/commands/autoSizeSingleColumn';
import { resizeTableColumn } from 'components/Table/model/commands/resizeTableColumn';
import { selectStickyColumnsWidth } from 'components/Table/model/selectors/selectStickyColumnsWidth';
import { selectVisibleColumnsWidth } from 'components/Table/model/selectors/selectVisibleColumnsWidth';
import { TABLE_COLUMN_MIN_WIDTH } from 'components/Table/tableSettings';
import { useEffect, useState } from 'react';
import type * as React from 'react';

interface Props {
  columnKey: string;
  sticky: boolean;
  width: number;
}

export const ColumnResizeHandle: React.FC<Props> = (props) => {
  const [dragDelta, setDragDelta] = useState<number | null>(null);

  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    columnWidths: selectVisibleColumnsWidth,
    containerHeight: maybePropN('tableContainerSize', 'height'),
    stickyColumnsWidth: selectStickyColumnsWidth,
    tableContainerRef: prop('tableContainerRef'),
  });

  const floating = useFloating({
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    strategy: 'absolute',
  });

  // trigger floating drag handle updates every time the column widths change:
  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.columnWidths): trigger update when widths repaint
  useEffect(() => {
    floating.update();
  }, [ctxProps.columnWidths, floating.update]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const startX = e.clientX;
    setDragDelta(0);
    const computeDragDelta = (x: number) =>
      Math.max(
        -(props.width - TABLE_COLUMN_MIN_WIDTH), // don't allow dragging to less than min column width
        x - startX,
      );
    const listeners = new ManagedDomEventListeners(
      [
        window,
        'mousemove',
        (ev: MouseEvent) => {
          setDragDelta(computeDragDelta(ev.clientX));
          ev.preventDefault();
        },
      ],
      [
        window,
        'mouseup',
        (ev: MouseEvent) => {
          listeners.remove();
          setDragDelta(null);
          ctxProps.dispatch(resizeTableColumn, {
            columnKey: props.columnKey,
            delta: computeDragDelta(ev.clientX),
          });
        },
      ],
    );
  };

  const handleDoubleClick = () => {
    ctxProps.dispatch(autoSizeSingleColumn, { key: props.columnKey });
  };

  const portalRoot =
    ctxProps.tableContainerRef?.current?.getElementsByClassName(
      'ipf-table-resize-handle-container',
    )[0] as HTMLDivElement | undefined;

  // The handle is hidden when it appears under sticky columns programmatically.
  // We didn't find a way to do this with CSS only.
  const hideHandleUnderSticky =
    !props.sticky && floating.x < ctxProps.stickyColumnsWidth;

  // The resize handle is rendered via Floating UI to avoid Chrome repaint glitches (see NIM-21939).
  return (
    <>
      <div
        // This div is there only to catch the reference to the header cell.
        className='hidden'
        ref={(el) => floating.refs.setReference(el?.parentElement ?? null)}
      />
      {portalRoot && (
        <FloatingPortal root={portalRoot}>
          {/* Interaction blocking overlay while dragging */}
          {dragDelta !== null && (
            <div className='fixed z-dragHandleBackdrop inset-0 select-none cursor-col-resize' />
          )}
          <div
            ref={floating.refs.setFloating}
            className={classNames(
              'column-resize-handle',
              dragDelta === null && hideHandleUnderSticky && 'invisible',
              dragDelta !== null &&
                'column-resize-handle--dragging z-dragHandle',
            )}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{
              ...floating.floatingStyles,
              height: `${ctxProps.containerHeight ?? 0}px`,
              transform: `${floating.floatingStyles.transform ?? ''} translateX(${(dragDelta ?? 0) - 3}px)`,
            }}
          />
        </FloatingPortal>
      )}
    </>
  );
};
